import type { TablePaginationConfig } from 'antd';
import { useState, useMemo } from 'react';

export type Pagination = Pick<TablePaginationConfig, 'onChange' | 'showSizeChanger' | 'showTotal'> &
  Required<Pick<TablePaginationConfig, 'pageSize' | 'current' | 'total'>>;

export type PaginationShape = {
  currentPage: number;
  pageSize: number;
};

export type TablePaginationShape = {
  paginationOptions: Pagination;
  setPaginationOptions: (item: PaginationShape) => void;
};

export const useTablePagination = (
  total: Pagination['total'],
  entityName?: string,
  defaultCurrentPage?: Pagination['current'],
  defaultPageSize?: Pagination['pageSize'],
): TablePaginationShape => {
  const [{ pageSize, currentPage }, setPaginationOptions] = useState<PaginationShape>({
    currentPage: defaultCurrentPage ?? 1,
    pageSize: defaultPageSize ?? 20,
  });

  const paginationOptions = useMemo(
    () => ({
      pageSize,
      current: currentPage,
      total,
      onChange: (newPage: number, newPageSize: number) =>
        setPaginationOptions({ pageSize: newPageSize, currentPage: newPage }),
      showSizeChanger: true,
      showTotal: (currentTotal: number, range: [number, number]) =>
        `${range[0]}-${range[1]} of ${currentTotal} ${entityName ?? 'items'}`,
    }),
    [currentPage, entityName, pageSize, total],
  );

  return { paginationOptions, setPaginationOptions };
};
